import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { useAccountId } from '../../../../../redux/slices/appInfo/hooks';
import { useVideoCreditGate } from '../../credits/useVideoCreditGate';
import { creditMessages } from '../../credits/creditMessages';
import { ReelRefusalCode } from './reelRefusal';
import { reelReviewMessages } from './reelReviewMessages';
import { aiBudgetMessages } from '../../aiBudgetMessages';
import { useUpgradePrompt } from '../../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../../config/music24PlanCapabilities';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the preview, the history and the sheets */
import styles from './ReelReview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelRefusedNoticeProps {
  code: ReelRefusalCode;
  /** ISO 8601. The start of the next quota month, from a 402. */
  resetsAt: string | null;
  /** "Regenerate later": closes the notice. The reel stays rejected and offers "Make another". */
  onClose: () => void;
}

/**
 * The reel is rejected, but no new reel started (design e3): 402 quota (red, with "Buy credits",
 * which opens the credit packs at once, owner rule 2026-10-06; the packs also hold the Upgrade
 * line), 403 no AI reels capability, 429 spent Free AI budget (with "Upgrade") and 503 bank too
 * small. The parent is rejected in every case.
 */
const ReelRefusedNoticeComponent: React.FC<ReelRefusedNoticeProps> = ({ code, resetsAt, onClose }) => {
  const intl = useIntl();
  const { offerCredits } = useVideoCreditGate(useAccountId());
  const buyCredits = useCallback(() => offerCredits(), [offerCredits]);
  const openUpgrade = useUpgradePrompt();
  const upgrade = useCallback(() => openUpgrade(SMLUpgradeFeature.aiPriority), [openUpgrade]);

  let body: string;
  if (code === 'M24_LIMIT_QUOTA') {
    body =
      resetsAt === null
        ? intl.formatMessage(reelReviewMessages.refusedQuota)
        : intl.formatMessage(reelReviewMessages.refusedQuotaDate, {
            date: intl.formatDate(resetsAt, { day: 'numeric', month: 'short' }),
          });
  } else if (code === 'M24_LIMIT_FEATURE') {
    body = intl.formatMessage(reelReviewMessages.refusedFeature);
  } else if (code === 'M24_AI_BUDGET_FREE_EXHAUSTED') {
    body = intl.formatMessage(aiBudgetMessages.failSentence);
  } else {
    body = intl.formatMessage(reelReviewMessages.refusedUnavailable);
  }

  return (
    <Alert color={code === 'M24_LIMIT_QUOTA' ? 'danger' : 'info'} className="mb-0" role="alert">
      <strong>{intl.formatMessage(reelReviewMessages.refusedTitle)}</strong>
      <br />
      {body}
      <div className={`${styles.actions} mt-2`}>
        {code === 'M24_LIMIT_QUOTA' && (
          <SMLButton color="primary" size="sm" omitCap onClick={buyCredits}>
            {intl.formatMessage(creditMessages.gateBuyCredits)}
          </SMLButton>
        )}
        {code === 'M24_AI_BUDGET_FREE_EXHAUSTED' && (
          <SMLButton color="primary" size="sm" omitCap onClick={upgrade}>
            {intl.formatMessage(aiBudgetMessages.upgrade)}
          </SMLButton>
        )}
        <SMLButton outline size="sm" omitCap onClick={onClose}>
          {intl.formatMessage(reelReviewMessages.regenerateLater)}
        </SMLButton>
      </div>
    </Alert>
  );
};

export const ReelRefusedNotice = TypedMemo(ReelRefusedNoticeComponent);
